import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Plus, Minus, AlertTriangle, Package, ShoppingCartIcon, X, Trash } from 'lucide-react';
import { useAppStore } from '../../shared/store';
import { generateId } from '../../shared/utils/id';
import { toast } from '../../shared/components/Toast';
import { ConfirmDialog } from '../../shared/components/ConfirmDialog';
import { InventoryItem } from '../../shared/types';
const CATEGORIES = ['Zuivel', 'Vlees', 'Groenten', 'Fruit', 'Dranken', 'Bakkerij', 'Conserven', 'Voorraadkast', 'Overig'];
const UNITS = ['stuk', 'kg', 'g', 'l', 'ml', 'pak'];
export function StockPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightedItemId = searchParams.get('highlight');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<InventoryItem | null>(null);
  // Add form state
  const [newName, setNewName] = useState('');
  const [newBrand, setNewBrand] = useState('');
  const [newCategory, setNewCategory] = useState('Voorraadkast');
  const [newQuantity, setNewQuantity] = useState(1);
  const [newUnit, setNewUnit] = useState('stuk');
  const [newMinQuantity, setNewMinQuantity] = useState(1);
  const [newMaxQuantity, setNewMaxQuantity] = useState(10);
  const [newPrice, setNewPrice] = useState(0);
  const [newLocation, setNewLocation] = useState('Voorraadkast');
  const [newBulkEligible, setNewBulkEligible] = useState(false);
  const {
    inventory,
    updateInventoryItem,
    addInventoryItem,
    removeInventoryItem,
    addShoppingListItem,
    updateShoppingListItem,
    shoppingList,
    stores
  } = useAppStore();
  const categories = useMemo(() => {
    return Array.from(new Set(inventory.map(item => item.category)));
  }, [inventory]);
  const lowStockCount = useMemo(() => inventory.filter(item => item.quantity <= item.minQuantity).length, [inventory]);
  useEffect(() => {
    if (!highlightedItemId) return;
    const highlightedItem = inventory.find(item => item.id === highlightedItemId);
    if (highlightedItem) {
      setSearchQuery(highlightedItem.name);
    }
    const timer = window.setTimeout(() => {
      setSearchQuery('');
      setSearchParams({}, {
        replace: true
      });
    }, 3000);
    return () => window.clearTimeout(timer);
  }, [highlightedItemId, inventory, setSearchParams]);
  const filteredItems = useMemo(() => {
    return inventory.filter(item => {
      const matchesSearch = !searchQuery || item.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
      const matchesStatus = selectedStatus === 'all' || selectedStatus === 'low' && item.quantity <= item.minQuantity || selectedStatus === 'medium' && item.quantity > item.minQuantity && item.quantity < (item.maxQuantity || Infinity) || selectedStatus === 'high' && item.maxQuantity && item.quantity >= item.maxQuantity;
      return matchesSearch && matchesCategory && matchesStatus;
    });
  }, [inventory, searchQuery, selectedCategory, selectedStatus]);
  const handleUpdateQuantity = (item: InventoryItem, change: number) => {
    const newQty = Math.max(0, item.quantity + change);
    const stockStatus = newQty <= item.minQuantity ? 'low' : newQty >= item.maxQuantity ? 'high' : 'medium';
    updateInventoryItem(item.id, {
      quantity: newQty,
      currentStock: newQty,
      stockStatus
    });
  };
  const handleAddToList = (item: InventoryItem) => {
    const quantityNeeded = Math.max(1, item.minQuantity + 1 - item.quantity);
    const existingListItem = shoppingList.find(listItem => !listItem.isCompleted && listItem.name.toLowerCase() === item.name.toLowerCase());
    if (existingListItem) {
      const missingFromList = Math.max(0, quantityNeeded - existingListItem.quantity);
      if (missingFromList === 0) {
        toast.success(`${item.name} staat al voldoende op je lijst`);
        return;
      }
      updateShoppingListItem(existingListItem.id, {
        quantity: existingListItem.quantity + missingFromList
      });
      toast.success(`${item.name} aangevuld op je lijst`);
      return;
    }
    addShoppingListItem({
      id: generateId(),
      name: item.name,
      quantity: quantityNeeded,
      unit: item.unit,
      estimatedPrice: item.price,
      store: stores[0]?.name || 'Onbekend',
      priority: 'medium',
      category: item.category,
      isCompleted: false
    });
    toast.success(`${item.name} toegevoegd: ${quantityNeeded} ${item.unit}`);
  };
  const resetAddForm = () => {
    setNewName('');
    setNewBrand('');
    setNewCategory('Voorraadkast');
    setNewQuantity(1);
    setNewUnit('stuk');
    setNewMinQuantity(1);
    setNewMaxQuantity(10);
    setNewPrice(0);
    setNewLocation('Voorraadkast');
    setNewBulkEligible(false);
  };
  const handleAddItem = () => {
    if (!newName.trim()) {
      toast.error('Vul een productnaam in');
      return;
    }
    addInventoryItem({
      id: generateId(),
      name: newName.trim(),
      brand: newBrand.trim() || undefined,
      category: newCategory,
      quantity: newQuantity,
      unit: newUnit,
      minQuantity: newMinQuantity,
      maxQuantity: newMaxQuantity,
      location: newLocation,
      lastRestocked: new Date().toISOString(),
      price: newPrice,
      bulkEligible: newBulkEligible,
      averageShelfLife: 30,
      consumptionRate: 0,
      storageRequirement: 1,
      currentStock: newQuantity,
      totalWeight: 0,
      stockStatus: newQuantity <= newMinQuantity ? 'low' : newQuantity >= newMaxQuantity ? 'high' : 'medium'
    });
    toast.success(`${newName} toegevoegd aan voorraad`);
    resetAddForm();
    setShowAddDialog(false);
  };
  return <div className="min-h-screen bg-background pb-24" data-id="element-1717" data-mp="6m37ycdi8w5z">
      {/* Header & Search */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border pt-6 pb-4 px-6 space-y-4" data-id="element-1718" data-mp="ucgpva2dlemu">
        <div data-id="element-1719" data-mp="zbjtcq2pjcsp">
          <h1 className="text-2xl font-bold text-foreground" data-id="element-1720" data-mp="z9z33cnfeg0n">Voorraad thuis</h1>
          <p className="mt-0.5 text-sm text-muted-foreground" data-id="element-1721" data-mp="lcaf3vy2asrd">Houd bij wat je hebt. We geven een seintje als iets bijna op is.</p>
        </div>

        <div className="relative" data-id="element-1722" data-mp="pf73k3aa9j38">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" data-id="element-1723" data-mp="361zwogq5gsnr" />
          <input type="text" placeholder="Zoek in voorraad..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3 bg-card border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent text-foreground placeholder-muted-foreground" data-id="element-1724" data-mp="3a3uu3a4ymlw9" />
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide" data-id="element-1725" data-mp="37jygws97t6mg">
          <button onClick={() => setSelectedStatus('all')} className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${selectedStatus === 'all' ? 'bg-primary text-primary-foreground' : 'bg-card border border-border text-muted-foreground hover:bg-accent'}`} data-id="element-1726" data-mp="2jsjb2pi8c76g">
            Alles
          </button>
          <button onClick={() => setSelectedStatus('low')} className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-1.5 ${selectedStatus === 'low' ? 'bg-warning text-warning-foreground' : 'bg-card border border-border text-muted-foreground hover:bg-accent'}`} data-id="element-1727" data-mp="2oqkrt3s6nuer">
            <AlertTriangle className="w-3.5 h-3.5" data-id="element-1728" data-mp="1fkl1j7iraoiv" />
            Bijna op{lowStockCount > 0 ? ` (${lowStockCount})` : ''}
          </button>
          <select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} className="px-4 py-2 bg-card border border-border rounded-full text-sm font-medium text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary" data-id="element-1729" data-mp="1kkpe4mgjmz3t">
            <option value="all" data-id="element-1730" data-mp="1yg1a0x6fqvmb">Alle Categorieën</option>
            {categories.map(c => <option key={c} value={c} data-id="element-1731" data-mp="23bglxjkvuhg3">
                {c}
              </option>)}
          </select>
        </div>
      </div>

      {lowStockCount > 1 && selectedStatus !== 'low' && <div className="px-6 pt-4" data-id="element-1732" data-mp="2c7xy8q4zgldf">
          <section className="flex items-center justify-between gap-3 rounded-2xl border border-warning/25 bg-warning/10 p-4" aria-label="Producten die bijna op zijn" data-id="element-1733" data-mp="2h83vclu0v02w">
            <div className="flex items-start gap-3" data-id="element-1734" data-mp="mfbb7aknfl2">
              <div className="mt-0.5 rounded-xl bg-warning/15 p-2 text-warning" data-id="element-1735" data-mp="lns6vy5vabo">
                <AlertTriangle className="h-5 w-5" data-id="element-1736" data-mp="3spgahw52wim0" />
              </div>
              <div data-id="element-1737" data-mp="3nreop3g9q56u">
                <h2 className="font-semibold text-foreground" data-id="element-1738" data-mp="3nrwuorsqfxry">{lowStockCount} producten zijn bijna op</h2>
                <p className="mt-0.5 text-sm text-muted-foreground" data-id="element-1739" data-mp="oayjlmtmfsd5">Zet ze op je lijst voordat je gaat winkelen.</p>
              </div>
            </div>
            <button onClick={() => setSelectedStatus('low')} className="shrink-0 rounded-xl bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90" data-id="element-1740" data-mp="kcsgqpvwlwxi">
              Bekijk producten
            </button>
          </section>
        </div>}

      {/* List */}
      <div className="px-6 pt-4 space-y-3" data-id="element-1741" data-mp="13893nkkby6kh">
        {filteredItems.length === 0 ? <div className="text-center py-12" data-id="element-1742" data-mp="140vnkvoitmvd">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent flex items-center justify-center" data-id="element-1743" data-mp="143rkcgomu5i8">
              <Package className="w-8 h-8 text-muted-foreground" data-id="element-1744" data-mp="z4x3hka86r0r" />
            </div>
            <p className="text-foreground font-medium" data-id="element-1745" data-mp="1d3ogf3dcq6nb">
              Geen producten gevonden
            </p>
            <p className="text-sm text-muted-foreground mt-1" data-id="element-1746" data-mp="192uo7xsvm5om">
              Pas je filters aan
            </p>
          </div> : <AnimatePresence data-id="element-1747" data-mp="1i282r4vuxkg7">
            {filteredItems.map(item => {
          const isLow = item.quantity <= item.minQuantity;
          const maxQ = item.maxQuantity || item.minQuantity * 2 || 10;
          const percent = Math.min(100, item.quantity / maxQ * 100);
          return <motion.div layout initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} transition={{
            duration: 0.2
          }} key={item.id} className={`bg-card border rounded-2xl p-4 flex flex-col gap-4 ${item.id === highlightedItemId ? 'border-primary ring-2 ring-primary/20' : isLow ? 'border-l-4 border-l-warning border-y-border border-r-border' : 'border-border'}`} data-id="element-1748" data-mp="25s2efj4fx2yv">
                  <div className="flex justify-between items-start gap-4" data-id="element-1749" data-mp="1wp6smvf2g6st">
                    <div className="flex-1 min-w-0" data-id="element-1750" data-mp="1iom4rkuanksg">
                      <h3 className="font-medium text-foreground truncate" data-id="element-1751" data-mp="1ip5ptaf8qpap">
                        {item.name}
                      </h3>
                      {item.brand && <p className="text-xs text-muted-foreground" data-id="element-1752" data-mp="3pdefwsjxoh57">
                          {item.brand}
                        </p>}
                    </div>

                    <div className="text-right w-24 flex-shrink-0" data-id="element-1753" data-mp="3fnuy7q7yitbx">
                      <p className="text-sm font-medium text-foreground" data-id="element-1754" data-mp="3blhrozldyre3">
                        {item.quantity}{' '}
                        <span className="text-muted-foreground font-normal" data-id="element-1755" data-mp="c6cpzho9vkaz">
                          {item.unit}
                        </span>
                      </p>
                      <div className="relative h-2 bg-secondary rounded-full mt-2 overflow-hidden" data-id="element-1756" data-mp="35ei2oapqsn4">
                        <div className={`absolute top-0 left-0 h-full rounded-full transition-all duration-300 ${isLow ? 'bg-warning' : percent > 80 ? 'bg-success' : 'bg-primary'}`} style={{
                    width: `${percent}%`
                  }} data-id="element-1757" data-mp="3ubpqj5i1yvoh" />
                        {/* Min quantity marker */}
                        <div className="absolute top-0 bottom-0 w-0.5 bg-background z-10" style={{
                    left: `${Math.min(100, item.minQuantity / maxQ * 100)}%`
                  }} data-id="element-1758" data-mp="13dmglylretqz" />
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between" data-id="element-1759" data-mp="1h4reav5khbyo">
                    <div className="flex items-center gap-1 bg-accent rounded-lg p-1" data-id="element-1760" data-mp="1m2t0hoyahred">
                      <button onClick={() => handleUpdateQuantity(item, -1)} disabled={item.quantity === 0} className="flex h-11 w-11 items-center justify-center rounded-md transition-colors hover:bg-background disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary" aria-label={`${item.name} met één verlagen`} data-id="element-1761" data-mp="1r48lxi4dpzxa">
                        <Minus className="w-4 h-4 text-foreground" data-id="element-1762" data-mp="2xknh12e5iy5x" />
                      </button>
                      <span className="w-8 text-center font-medium text-foreground text-sm" data-id="element-1763" data-mp="3bjwqzkbrkw1g">
                        {item.quantity}
                      </span>
                      <button onClick={() => handleUpdateQuantity(item, 1)} className="flex h-11 w-11 items-center justify-center rounded-md transition-colors hover:bg-background focus:outline-none focus:ring-2 focus:ring-primary" aria-label={`${item.name} met één verhogen`} data-id="element-1764" data-mp="36iz7n4f0pvyd">
                        <Plus className="w-4 h-4 text-foreground" data-id="element-1765" data-mp="2ntslt8ixgo8c" />
                      </button>
                    </div>

                    <div className="flex items-center gap-2" data-id="element-1766" data-mp="202wx4k9rq95b">
                      {isLow && <button onClick={() => handleAddToList(item)} className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary" data-id="element-1767" data-mp="1v3sp3wykyhgk">
                          <ShoppingCartIcon className="h-3.5 w-3.5" data-id="element-1768" data-mp="5pkp7wxwtj1k" />
                          Zet op lijst
                        </button>}
                      <button onClick={() => setItemToDelete(item)} className="p-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg transition-colors" data-id="element-1769" data-mp="yd6s12zbcjnd">
                        <Trash className="w-4 h-4" data-id="element-1770" data-mp="paxjujrip2d5" />
                      </button>
                    </div>
                  </div>
                </motion.div>;
        })}
          </AnimatePresence>}
      </div>

      {/* FAB */}
      <button onClick={() => setShowAddDialog(true)} className="fixed bottom-24 right-6 w-14 h-14 bg-primary text-primary-foreground rounded-full shadow-lg shadow-primary/30 flex items-center justify-center hover:scale-105 transition-transform z-20" data-id="element-1771" data-mp="1fnao21oz9jye">
        <Plus className="w-6 h-6" data-id="element-1772" data-mp="11vcnwgd8uyed" />
      </button>

      {/* Add Item Dialog */}
      <AnimatePresence data-id="element-1773" data-mp="11t9xvwc3d59n">
        {showAddDialog && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={() => setShowAddDialog(false)} data-id="element-1774" data-mp="wxb41t7zuv1u">
            <motion.div initial={{
          y: 100,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: 100,
          opacity: 0
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} onClick={e => e.stopPropagation()} className="w-full sm:max-w-md bg-card border border-border rounded-t-2xl sm:rounded-2xl p-6 max-h-[85vh] overflow-y-auto" data-id="element-1775" data-mp="2hb3dbj424uvi">
              <div className="flex items-center justify-between mb-6" data-id="element-1776" data-mp="3cbd9c56c4g4x">
                <div className="flex items-center gap-3" data-id="element-1777" data-mp="3h96dkso2v02k">
                  <div className="p-2 bg-primary/10 rounded-xl" data-id="element-1778" data-mp="3la8dlbptjg5l">
                    <Package className="w-5 h-5 text-primary" data-id="element-1779" data-mp="3lc30gvknhicb" />
                  </div>
                  <h2 className="text-lg font-semibold text-foreground" data-id="element-1780" data-mp="3qacx7am0wr01">
                    Product Toevoegen
                  </h2>
                </div>
                <button onClick={() => setShowAddDialog(false)} className="p-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors" data-id="element-1781" data-mp="15ib16mndyyvc">
                  <X className="w-5 h-5" data-id="element-1782" data-mp="c8h0trhayzr3" />
                </button>
              </div>

              <div className="space-y-4" data-id="element-1783" data-mp="pzm5x93fwk8t">
                <div className="space-y-1.5" data-id="element-1784" data-mp="q0ox7i4ocop9">
                  <label className="text-sm font-medium text-foreground" data-id="element-1785" data-mp="l7lykvl1ip27">
                    Naam *
                  </label>
                  <input type="text" value={newName} onChange={e => setNewName(e.target.value)} placeholder="bijv. Melk, Brood, Eieren..." className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent text-foreground placeholder-muted-foreground" autoFocus data-id="element-1786" data-mp="zv71aoe06m9l" />
                </div>

                <div className="space-y-1.5" data-id="element-1787" data-mp="36oj96m7stp22">
                  <label className="text-sm font-medium text-foreground" data-id="element-1788" data-mp="36pkhwk1cdzgm">
                    Merk
                  </label>
                  <input type="text" value={newBrand} onChange={e => setNewBrand(e.target.value)} placeholder="Optioneel" className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent text-foreground placeholder-muted-foreground" data-id="element-1789" data-mp="30le9l7razgts" />
                </div>

                <div className="grid grid-cols-2 gap-3" data-id="element-1790" data-mp="2hvy6cco8ha9o">
                  <div className="space-y-1.5" data-id="element-1791" data-mp="23v5fq311yzbu">
                    <label className="text-sm font-medium text-foreground" data-id="element-1792" data-mp="23wgj41ql652f">
                      Categorie
                    </label>
                    <select value={newCategory} onChange={e => setNewCategory(e.target.value)} className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:ring-2 focus:ring-primary text-foreground" data-id="element-1793" data-mp="23iivkuhca7g">
                      {CATEGORIES.map(c => <option key={c} value={c} data-id="element-1794" data-mp="1fejerbx6otxu">
                          {c}
                        </option>)}
                    </select>
                  </div>
                  <div className="space-y-1.5" data-id="element-1795" data-mp="15nyg5ohifpex">
                    <label className="text-sm font-medium text-foreground" data-id="element-1796" data-mp="1td9burk06tm7">
                      Eenheid
                    </label>
                    <select value={newUnit} onChange={e => setNewUnit(e.target.value)} className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:ring-2 focus:ring-primary text-foreground" data-id="element-1797" data-mp="1yb1a03b8rbyu">
                      {UNITS.map(u => <option key={u} value={u} data-id="element-1798" data-mp="8zipfp2nza08">
                          {u}
                        </option>)}
                    </select>
                  </div>
                </div>

                <div data-id="element-1799" data-mp="g5oikdz6a69">
                  <p className="mb-3 text-sm leading-relaxed text-muted-foreground" data-id="element-1800" data-mp="gx8sz24glh8">
                    Kies wanneer je een seintje wilt en hoeveel je maximaal wilt bewaren.
                  </p>
                  <div className="grid grid-cols-3 gap-3" data-id="element-1801" data-mp="ads3t7z6s3qr">
                    <div className="space-y-1.5" data-id="element-1802" data-mp="ach35h11fc3v">
                      <label className="text-sm font-medium text-foreground" data-id="element-1803" data-mp="3iv6j5omv7kbh">
                        Nu in huis
                      </label>
                      <input type="number" min="0" value={newQuantity} onChange={e => setNewQuantity(parseInt(e.target.value) || 0)} className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:ring-2 focus:ring-primary text-foreground" data-id="element-1804" data-mp="3dsxo8mqfm86q" />
                      <p className="text-xs text-muted-foreground" data-id="element-1805" data-mp="3krmzqmxofdzc">Wat je nu hebt</p>
                    </div>
                    <div className="space-y-1.5" data-id="element-1806" data-mp="3osyt93jo8jct">
                      <label className="text-sm font-medium text-foreground" data-id="element-1807" data-mp="3tl00gb1zfjvz">
                        Waarschuw bij
                      </label>
                      <input type="number" min="0" value={newMinQuantity} onChange={e => setNewMinQuantity(parseInt(e.target.value) || 0)} className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:ring-2 focus:ring-primary text-foreground" data-id="element-1808" data-mp="2ddqk0oo6l6o" />
                      <p className="text-xs text-muted-foreground" data-id="element-1809" data-mp="q4ji3d3z31wt">Of minder</p>
                    </div>
                    <div className="space-y-1.5" data-id="element-1810" data-mp="v347sillm370">
                      <label className="text-sm font-medium text-foreground" data-id="element-1811" data-mp="v1t6qqjjf9jl">
                        Ruimte voor
                      </label>
                      <input type="number" min="1" value={newMaxQuantity} onChange={e => setNewMaxQuantity(parseInt(e.target.value) || 1)} className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:ring-2 focus:ring-primary text-foreground" data-id="element-1812" data-mp="2amln7ahlzglq" />
                      <p className="text-xs text-muted-foreground" data-id="element-1813" data-mp="1ruj0piiad63k">Maximaal thuis</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-1.5" data-id="element-1814" data-mp="1hy6d92dc3oh9">
                  <label className="text-sm font-medium text-foreground" data-id="element-1815" data-mp="1hzhbynrxa61i">
                    Prijs (€)
                  </label>
                  <input type="number" min="0" step="0.01" value={newPrice} onChange={e => setNewPrice(parseFloat(e.target.value) || 0)} className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:ring-2 focus:ring-primary text-foreground" data-id="element-1816" data-mp="3cbnxogig0582" />
                </div>

                <div className="space-y-1.5" data-id="element-1817" data-mp="3fbfsntjc32sy">
                  <label className="text-sm font-medium text-foreground" data-id="element-1818" data-mp="31lc8r97xfl6o">
                    Locatie in huis
                  </label>
                  <select value={newLocation} onChange={e => setNewLocation(e.target.value)} className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:ring-2 focus:ring-primary text-foreground" data-id="element-1819" data-mp="36e5eo0f4szkc">
                    <option value="Voorraadkast" data-id="element-1820" data-mp="2injjh9q879rr">Voorraadkast</option>
                    <option value="Koelkast" data-id="element-1821" data-mp="2mouyymfdyem8">Koelkast</option>
                    <option value="Vriezer" data-id="element-1822" data-mp="2mqftic1p9xk3">Vriezer</option>
                    <option value="Kelder" data-id="element-1823" data-mp="24141e72ydvfg">Kelder</option>
                    <option value="Badkamer" data-id="element-1824" data-mp="1z1hojyr1eh76">Badkamer</option>
                    <option value="Schoonmaakkast" data-id="element-1825" data-mp="1z32lfv0tqc87">Schoonmaakkast</option>
                  </select>
                </div>

                <label className="flex items-center gap-3 p-4 bg-background border border-border rounded-xl cursor-pointer hover:bg-accent transition-colors" data-id="element-1826" data-mp="281wb11fqm2c9">
                  <input type="checkbox" checked={newBulkEligible} onChange={e => setNewBulkEligible(e.target.checked)} className="w-5 h-5 rounded border-border text-primary focus:ring-primary" data-id="element-1827" data-mp="2mxm058g7yvl7" />
                  <div data-id="element-1828" data-mp="2rr6sokz1mxd0">
                    <p className="text-sm font-medium text-foreground" data-id="element-1829" data-mp="2rrzzfly05mv5">
                      Grootverpakking mogelijk
                    </p>
                    <p className="text-xs text-muted-foreground" data-id="element-1830" data-mp="30r3hqnpchy8l">
                      Kijk ook naar voordeelverpakkingen
                    </p>
                  </div>
                </label>
              </div>

              <div className="flex gap-3 mt-6" data-id="element-1831" data-mp="22fsln3ix7moa">
                <button onClick={() => setShowAddDialog(false)} className="flex-1 py-3 bg-accent text-foreground rounded-xl font-medium hover:bg-accent/80 transition-colors" data-id="element-1832" data-mp="22cehbpqp53e7">
                  Annuleren
                </button>
                <button onClick={handleAddItem} className="flex-1 py-3 bg-primary text-primary-foreground rounded-xl font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2" data-id="element-1833" data-mp="2h3docwbvvjvo">
                  <Plus className="w-4 h-4" data-id="element-1834" data-mp="2v3og01l0p2hw" />
                  Toevoegen
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      <ConfirmDialog isOpen={!!itemToDelete} title="Product verwijderen" description={`Weet je zeker dat je "${itemToDelete?.name}" uit je voorraad wilt verwijderen?`} confirmText="Verwijderen" cancelText="Annuleren" variant="danger" onConfirm={() => {
      if (itemToDelete) {
        removeInventoryItem(itemToDelete.id);
        toast.success(`${itemToDelete.name} verwijderd`);
        setItemToDelete(null);
      }
    }} onCancel={() => setItemToDelete(null)} data-id="element-1835" data-mp="10olueez3a4k9" />
    </div>;
}